/** Cart context — localStorage persistence; works for guests and logged-in users. */
import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);
const KEY = "nvl-cart-v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
  });

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  const add = useCallback((line) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.kind === line.kind && x.item_id === line.item_id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + (line.quantity || 1) };
        return next;
      }
      return [...prev, { quantity: 1, ...line }];
    });
  }, []);

  const update = useCallback((kind, item_id, quantity) => {
    setItems((prev) => prev.map((x) =>
      x.kind === kind && x.item_id === item_id ? { ...x, quantity: Math.max(1, quantity) } : x
    ));
  }, []);

  const remove = useCallback((kind, item_id) => {
    setItems((prev) => prev.filter((x) => !(x.kind === kind && x.item_id === item_id)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(() => items.reduce((s, x) => s + (x.price_inr || 0) * (x.quantity || 1), 0), [items]);
  const count = useMemo(() => items.reduce((s, x) => s + (x.quantity || 1), 0), [items]);

  const value = { items, add, update, remove, clear, subtotal, count };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
